import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Pipe,
  PipeTransform,
  Type,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { NbWindowService } from '@nebular/theme';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  FieldTypeConfig,
  FormlyFieldConfig,
  ɵobserve as observe,
} from '@ngx-formly/core';
import {
  FormlyFieldSelectProps,
  FormlySelectOption,
} from '@ngx-formly/core/select';
import { FieldType, FormlyFieldProps } from '@ngx-formly/material/form-field';
import { filter, isObservable, of, startWith, switchMap, tap } from 'rxjs';

interface SelectProps extends FormlyFieldProps, FormlyFieldSelectProps {
  multiple?: boolean;
  selectAllOption?: string;
  disableOptionCentering?: boolean;
  typeaheadDebounceInterval?: number;
  compareWith?: (o1: any, o2: any) => boolean;
}

export interface FormlySelectFieldConfig extends FormlyFieldConfig<SelectProps> {
  type: 'select' | Type<SelectAutocompleteComponent>;
}

@UntilDestroy()
@Component({
  selector: 'vmspdb-component-select-autocomplete',
  templateUrl: './select-autocomplete.component.html',
  styleUrls: ['./select-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SelectAutocompleteComponent
  extends FieldType<FieldTypeConfig>
  implements AfterViewInit
{
  protected win = inject(NbWindowService);

  filteredOptions: FormlySelectOption[] = [];

  searchControl = new FormControl<string>('');

  ngAfterViewInit(): void {
    (isObservable(this.props.options)
      ? this.props.options
      : of(this.props.options)
    )
      .pipe(
        untilDestroyed(this),
        switchMap((options) =>
          this.searchControl.valueChanges.pipe(
            startWith(''),
            tap((value) => this.updateFilters(options ?? [], value ?? '')),
          ),
        ),
      )
      .subscribe();
  }

  addModal($event: MouseEvent) {
    $event.stopPropagation();
    this.win
      .open(this.props['modal'], {
        title: this.props['addTitle'] ?? 'Add',
        windowClass: this.props['windowClass'] ?? 'modal-medium',
        context: this.props['windowContext'] ?? {},
      })
      .onClose.pipe(
        untilDestroyed(this),
        filter((value) => value),
        tap((uuid) => {
          this.formControl.setValue(uuid);
        }),
      )
      .subscribe();
  }

  @ViewChild(MatSelect, { static: true }) set select(select: any) {
    observe(select, ['_parentFormField', '_textField'], ({ currentValue }) => {
      if (currentValue) {
        select._preferredOverlayOrigin =
          select._parentFormField.getConnectedOverlayOrigin();
      }
    });
  }
  override defaultOptions = {
    props: {
      compareWith(o1: any, o2: any) {
        return o1 === o2;
      },
    },
  };

  private selectAllValue!: { options: any; value: any[] };

  getSelectAllState(options: any[]) {
    if (this.empty || this.value.length === 0) {
      return 'unchecked';
    }

    return this.value.length !== this.getSelectAllValue(options).length
      ? 'indeterminate'
      : 'checked';
  }

  toggleSelectAll(options: any[]) {
    const selectAllValue = this.getSelectAllValue(options);
    this.formControl.setValue(
      !this.value || this.value.length !== selectAllValue.length
        ? selectAllValue
        : [],
    );
    this.formControl.markAsDirty();
  }

  change($event: MatSelectChange) {
    this.props.change?.(this.field, $event);
  }

  _getAriaLabelledby() {
    if (this.props.attributes?.['aria-labelledby']) {
      return this.props.attributes['aria-labelledby'] as string;
    }

    return this.formField?._labelId;
  }

  _getAriaLabel() {
    return this.props.attributes?.['aria-label'] as string;
  }

  private getSelectAllValue(options: any[]) {
    if (!this.selectAllValue || options !== this.selectAllValue.options) {
      const flatOptions: any[] = [];
      options.forEach((o) =>
        o.group ? flatOptions.push(...o.group) : flatOptions.push(o),
      );

      this.selectAllValue = {
        options,
        value: flatOptions.filter((o) => !o.disabled).map((o) => o.value),
      };
    }

    return this.selectAllValue.value;
  }

  setFilteredOptions($event: Record<string, string>[]) {
    // TODO: Look into infinite scroll for mat-select with persistence for selected option.
    this.filteredOptions = $event as unknown as FormlySelectOption[];
  }

  private updateFilters(options: any[], value: string) {
    if (value === '') {
      this.setFilteredOptions(options);
      return;
    }

    this.setFilteredOptions(
      options.filter((option) =>
        option.label?.toLowerCase()?.includes(value.toLowerCase()),
      ),
    );
  }
}

@Pipe({
  name: 'formlyOptionToRecord',
  standalone: false,
})
export class FormlyOptionToRecordPipe implements PipeTransform {
  transform(value: FormlySelectOption[]): any {
    return value as unknown as Record<string, string>;
  }
}
